import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import LazyChunkSuspense from '../components/LazyChunkSuspense';
import { createRecoverableLazyLoader } from '../utils/chunkErrorHandler';

const GOOGLE_MAPS_API_KEY = (process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '').trim();

const GoogleMapsScriptLoaderLazy = React.lazy(
  createRecoverableLazyLoader(async () => {
    const m = await import('./GoogleMapsScriptLoader');
    return { default: m.GoogleMapsScriptLoader };
  })
);

interface GoogleMapsContextType {
  isLoaded: boolean;
  loadError: Error | undefined;
}

const GoogleMapsContext = createContext<GoogleMapsContextType>({
  isLoaded: false,
  loadError: undefined,
});

const GoogleMapsNeedsContext = createContext<(active: boolean) => void>(() => {});

export const useGoogleMaps = () => useContext(GoogleMapsContext);

/** Enregistre un composant consommateur de Google Maps (charge le SDK au premier usage). */
export function useGoogleMapsConsumer(): void {
  useGoogleMapsRequest(true);
}

/** Demande le chargement du SDK Maps tant que `active` est vrai. */
export function useGoogleMapsRequest(active: boolean): void {
  const setNeeds = useContext(GoogleMapsNeedsContext);
  useEffect(() => {
    if (!active) return undefined;
    setNeeds(true);
    return () => setNeeds(false);
  }, [active, setNeeds]);
}

const hasValidMapsKey =
  GOOGLE_MAPS_API_KEY.length > 0 &&
  GOOGLE_MAPS_API_KEY !== 'undefined' &&
  GOOGLE_MAPS_API_KEY !== 'null';

export const GoogleMapsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [consumers, setConsumers] = useState(0);
  const [mapsState, setMapsState] = useState<GoogleMapsContextType>({
    isLoaded: false,
    loadError: undefined,
  });

  const setNeeds = useCallback((active: boolean) => {
    setConsumers(count => Math.max(0, count + (active ? 1 : -1)));
  }, []);

  const shouldLoadScript = hasValidMapsKey && consumers > 0;

  const handleMapsState = useCallback((state: GoogleMapsContextType) => {
    setMapsState(state);
  }, []);

  useEffect(() => {
    if (!shouldLoadScript) {
      setMapsState({ isLoaded: false, loadError: undefined });
    }
  }, [shouldLoadScript]);

  return (
    <GoogleMapsNeedsContext.Provider value={setNeeds}>
      <GoogleMapsContext.Provider value={mapsState}>
        {shouldLoadScript ? (
          <LazyChunkSuspense fallback={null}>
            <GoogleMapsScriptLoaderLazy onState={handleMapsState} />
          </LazyChunkSuspense>
        ) : null}
        {children}
      </GoogleMapsContext.Provider>
    </GoogleMapsNeedsContext.Provider>
  );
};
